import { capitalize } from 'lodash-es'
import moment from 'moment'
import React from 'react'
import NumberFormat from 'react-number-format'
import { v4 as uuidv4 } from 'uuid'
import { ADDON_ONLY, BAR_VIP_MEMBERSHIP_TYPES, BOARDING, DAYCARE, GROOMING } from './constants'
import { gray, purple } from './colors'

export const BASIC_MEMBERSHIP_LABEL = 'Basic Plan'

export const isBasicMembership = (membership) => {
	if (!membership) return false
	if (membership.bucket === 'basic') return true

	return (membership.type || '').toLowerCase().includes('basic')
}

export const getBasicMembershipInfo = () => ({
	type: BASIC_MEMBERSHIP_LABEL,
	name: BASIC_MEMBERSHIP_LABEL,
	bucket: 'basic'
})

export const generateUniqueId = () => {
	return uuidv4()
}

export const formatNumsToDollars = (amountInCents, withDollarTxt = true) => {
	if (amountInCents !== undefined && amountInCents !== null) {
		return `${withDollarTxt ? '$' : ''}${(amountInCents / 100).toFixed(2)}`
	}

	return ''
}

export const onlyNumbers = (value) => {
	if (!value || value === '0') return +value
	const onlyNums = value.toString().replace(/[^\d]/g, '').replace(/^0+/, '')
	if (onlyNums.length) return +onlyNums

	return 0
}

export const formatNthDecimals = (value, decimals = 2) => {
	if (value !== undefined && value !== null) {
		return (+value).toFixed(decimals)
	}

	return 0
}

const NumberFormatCustom = (props) => {
	const { inputRef, onChange, ...other } = props

	return (
		<NumberFormat
			{...other}
			getInputRef={inputRef}
			onValueChange={(values) => {
				onChange({
					target: {
						value: values.value
					}
				})
			}}
			isNumericString
			// prefix="$"
		/>
	)
}

export const NumberFormatDecimal = (props) => {
	return NumberFormatCustom({ ...props, decimalScale: 2 })
}

export const NumberFormatFixed = (props) => {
	return NumberFormatCustom({ ...props, decimalScale: 0 })
}

export const getServiceEarlyDropOffTime = (service) => {
	const earlyDropOffRangeText = service?.early_dropoff_hours || '6am - 7am'

	return earlyDropOffRangeText
}

export const formatMembershipType = (membershipType) => {
	const chunks = membershipType.split('_').map((n) => capitalize(n))
	const billingMethod = chunks.pop()
	const name = chunks

	return `${name.join(' ')} - ${billingMethod}`
}

export const checkIfNeedsRenewalAndShouldBeNotified = (household, memberships) => {
	const validMembs = getValidMemberships(memberships)

	// check all the non-notification cases
	if (!validMembs || !validMembs.length) {
		return [false, false]
	} else {
		if (validMembs[0].auto_renew) return [false, false]

		let daysTillExp = Math.abs(moment(validMembs[0].end_date).diff(moment(), 'days'))
		if (daysTillExp > 30) return [false, false]

		if (household.hasUpcomingMemberships) {
			return [false, false]
		} else {
			if (household.mute_renewal_alert) return [true, false]
			return [true, true]
		}
	}
}

export const checkIfPetIsBanned = (p, type) => {
	const banCondition =
		(p.temporary_service_restrictions &&
			p.temporary_service_restrictions.length &&
			p.temporary_service_restrictions.includes(type)) ||
		(p.permanent_service_restrictions &&
			p.permanent_service_restrictions.length &&
			p.permanent_service_restrictions.includes(type))
	return banCondition
}

export const checkIfPetHasCompletedGrooming = (p) => {
	const hasCompletedGrooming = p?.hasCompletedGrooming ? p.hasCompletedGrooming : false
	return hasCompletedGrooming
}

export const getIconAndTagByFoundingAndMembership = (household) => {
	let icon = null,
		color = null,
		tag = ''
	// Currently, the member tags that appear across the Facility Admin site and the TM Pet Care App have two elements: (1) The icon, (2) the tag text.
	// The icon section of the membership tags can have one of three states:
	// If a contact or dog belongs to a household with their founding toggle on(regardless of their membership type) - their icon will be a 'medal' in a gold color.
	const activeMembership =
		household.memberships && household.memberships.length && household.memberships[0]
			? household.memberships[0]
			: null
	const foundingMember = !!household?.founding_member
	if (foundingMember) {
		icon = 'medal'
		color = gray[800]
	}
	// If a contact or dog belongs to a household with their founding toggle off, and they DO have an active paid membership - then their icon will be a 'circle checkmark' in a purple color.
	else if (!!activeMembership) {
		icon = 'check-circle'
		color = purple[500]
	}
	// If a contact or dog belongs to a household with their founding toggle off, and they DO NOT have an active paid membership - then they will have no icon appear.
	else {
		icon = color = null
	}

	// console.log('mem...', activeMembership)

	// The tag text section of the membership tags can have one of two states:
	// If a contact or dog belongs to a household that has an active paid membership - then their text will say [Membership Type] - [Payment Term]. Ex. Pet Care - Monthly
	if (!!activeMembership) tag = formatMembershipType(activeMembership.type)
	// If a contact or dog belongs to a household that does not have an active paid membership - then their text will be blank.
	else tag = ''

	// Ex.So if a HH has their founding toggle off and they have a basic membership, then their will be no membership tag shown.

	return [icon, color, tag]
}

export const sortByKey =
	(keyGetter, descending = false) =>
	(left, right) => {
		const l = keyGetter(left)
		const r = keyGetter(right)
		if (l < r) {
			return descending ? 1 : -1
		}
		if (r < l) {
			return descending ? -1 : 1
		}

		return 0
	}

export const getTableRowId = (propsData) => {
	const data = propsData?.data || propsData
	const id = data._id || data?.tableData?.id

	return id
}

export const getSpecificAreas = (facility) => {
	const areas = facility.areas || []
	const indoor = []
	const outdoor = []
	const kennelRooms = []
	for (const area of areas) {
		if (area.type === 'Indoor Play') indoor.push(area)
		else if (area.type === 'Outdoor Play') outdoor.push(area)
		else if (area.type === 'Kennel Room' || area.type?.toLowerCase().includes('groom')) kennelRooms.push(area)
	}

	return { indoor, outdoor, kennelRooms }
}

/**
 * Returns a boolean if the client has a day pass
 * @param {Object} checkIn - The checkIn object
 * @param {array=} checkIn.day_passes
 * @param {string=} checkIn.daypass_id
 */
const hasDayPass = (checkIn) => {
	let has = false
	if (checkIn) {
		has = (checkIn.day_passes && checkIn.day_passes.length) || checkIn.daypass_id > ''
	}
	return has
}

/**
 * Returns an array of active, non expired membership objects
 * @param {object[]} memberships - an array of membership objects
 * @param {string[]=} types - an array of membership types to filter by, filters by all types if not specified; ex ['bar_annual', 'vip_annual']
 */
export const getValidMemberships = (memberships = [], types = []) => {
	const valid = memberships.filter((m) => {
		// Inclusive of the final day: a membership ending today is still valid today.
		const isNotExpiredByDate = moment(m.end_date).endOf('day').isSameOrAfter(moment())
		if (types.length > 0) {
			return types.includes(m.type) && !m.deactivated && !m.expired && isNotExpiredByDate
		} else {
			return !m.deactivated && !m.expired && isNotExpiredByDate
		}
	})

	const sorted = valid && valid.length ? valid.sort((a, b) => b.createdAt - a.createdAt) : []

	return sorted
}

export const validateNoPassCheckIn = (checkIn, shouldChargeForFirstCheckIn, market) => {
	let clientNeedsPass = true

	if (!checkIn.pets || !checkIn.pets.length) clientNeedsPass = false

	const clientHasDayPass = hasDayPass(checkIn)
	const barOrVipMemberships = getValidMemberships(checkIn.memberships, BAR_VIP_MEMBERSHIP_TYPES)
	const clientHasBarOrVip = barOrVipMemberships.length > 0

	if (clientHasBarOrVip || clientHasDayPass) clientNeedsPass = false

	return { needsPass: clientNeedsPass, hasBarOrVip: clientHasBarOrVip, hasDayPass: clientHasDayPass }
}

export const timeFormat = (timeAsString) => {
	const chunks = timeAsString.split(':')
	if (chunks.length > 1) {
		if (+chunks[1] !== 0) {
			return 'h:mma'
		}
	}

	return 'ha'
}

export const sortAlphaNum = (a, b, numericFist = true) => {
	var reA = /[^a-zA-Z]/g
	var reN = /[^0-9]/g

	var AInt = parseInt(a, 10)
	var BInt = parseInt(b, 10)

	if (isNaN(AInt) && isNaN(BInt)) {
		var aA = a.replace(reA, '')
		var bA = b.replace(reA, '')
		if (aA === bA) {
			var aN = parseInt(a.replace(reN, ''), 10)
			var bN = parseInt(b.replace(reN, ''), 10)
			return aN === bN ? 0 : aN > bN ? 1 : -1
		} else {
			return aA > bA ? 1 : -1
		}
	} else if (isNaN(AInt)) {
		//A is not an Int
		return numericFist ? 1 : -1 //to make alphanumeric sort first return -1 here
	} else if (isNaN(BInt)) {
		//B is not an Int
		return numericFist ? -1 : 1 //to make alphanumeric sort first return 1 here
	} else {
		return AInt > BInt ? 1 : -1
	}
}

export const capitilizeMultipleWords = (word = '', splitBy = '-', joinBy = ' ') => {
	return word
		.split(splitBy)
		.map((w) => capitalize(w))
		.join(joinBy)
}

export const getElegibility = (household = {}, pets = []) => {
	const { market_id, default_market_id, default_facility_id, contact = {}, memberships = [] } = household || {}
	const { address = {} } = contact
	// The HH must have their Location entered (home address, default facility)
	const flag1 = !!((market_id || default_market_id) && default_facility_id && address?.address_line_1)
	// The HH must not have an active paid membership (anything besides 'Basic Member'), or ever had an active paid membership in the past.
	const flag2 = memberships.every((m) => m.type !== 'vip_trial')
	// HH must have at least one dog profile added to their HH.
	const dogs = pets.filter((p) => p.animal_type === 'Dog')
	const flag3 = dogs.length > 0
	// The dog must have a vet on file
	const flag4 = flag3 ? dogs.some((p) => p.vet_clinic) : true
	// The dog must be over 16 weeks old
	const today = moment()
	const flag5 = flag3 ? dogs.some((p) => today.diff(p.birthday, 'week') >= 16) : true
	// The dog must be spayed/ neutered -OR- have an exemption that is not expired -IF- they are over 6 months old.
	const flag6 = flag3
		? dogs.some((p) => {
				const fixedSex = (p.sex || '').toLowerCase()
				const age = today.diff(p.birthday, 'month')
				const over6Months = age >= 6
				const exemptionExpired = today.diff(p.spay_neuter_deadline, 'day') < 0

				return over6Months
					? fixedSex.includes('spayed') || fixedSex.includes('neutered') || !exemptionExpired
					: true
		  })
		: true
	// The dog is not banned from the bar or pet care.
	const flag7 = flag3
		? dogs.some((p) => {
				const restrictions = (p.temporary_service_restrictions || []).concat(
					p.permanent_service_restrictions || []
				)

				return !restrictions.includes('Bar & Park') && !restrictions.includes('Daycare & Boarding')
		  })
		: true

	console.log({ flag1, flag2, flag3, flag4, flag5, flag6, flag7 })

	return {
		pass: flag1 && flag2 && flag3 && flag4 && flag5 && flag6 && flag7,
		flag1,
		flag2,
		flag3,
		flag4,
		flag5,
		flag6,
		flag7
	}
}

export const householdHasVIPTrial = (memberships = []) => {
	let hasVIPTrial = false
	if (memberships.length && memberships[0].type === 'vip_trial') {
		hasVIPTrial = true
	}

	return hasVIPTrial
}

/**
 * Whether a boarding meal/med time block applies on a given visit date,
 * based on drop-off/pick-up options (early dropoff, first/last day daycare, late pickup).
 */
export const isBoardingTimeBlockApplicable = (visit, date, time) => {
	if (!visit || visit.type !== BOARDING || !date || !time) {
		return true
	}

	const dates = visit.dates || []
	if (!dates.length) {
		return true
	}

	const firstDay = dates[0]
	const lastDay = dates[dates.length - 1]
	const isFirstDay = date === firstDay
	const isLastDay = date === lastDay

	if (!isFirstDay && !isLastDay) {
		return true
	}

	if (isFirstDay) {
		if (time === 'morning') {
			return !!visit.early_dropoff || !!visit.breakfast_available
		}
		if (time === 'midday') {
			return !!visit.first_day_daycare
		}
		if (time === 'evening') {
			return true
		}
		return false
	}

	if (isLastDay) {
		if (time === 'morning') {
			return true
		}
		if (time === 'midday') {
			return !!visit.last_day_daycare
		}
		if (time === 'evening') {
			return !!visit.last_day_daycare && !!visit.extended_pickup
		}
		return false
	}

	return true
}

/**
 * Whether a med/meal time block can be scheduled on a given visit date.
 * Boarding uses first/last-day drop-off and pickup rules; daycare uses visit options.
 */
export const isMedTimeBlockApplicable = (visit, date, time) => {
	if (!visit || !date || !time) {
		return true
	}

	if (visit.type === BOARDING) {
		return isBoardingTimeBlockApplicable(visit, date, time)
	}

	if (visit.type === DAYCARE || visit.type === 'compatibility_session') {
		const allowedTimes = ['midday']
		if (visit.early_dropoff) {
			allowedTimes.push('morning')
		}
		if (visit.extended_pickup) {
			allowedTimes.push('evening')
		}
		return allowedTimes.includes(time)
	}

	return true
}

export const filterAddOnsByTimeBlock = (addOnItem, visit) => {
	if (!addOnItem) return false

	const allowedTimeBlocks = visit?.allowedTimeBlocks || []
	if (!allowedTimeBlocks) return true

	if (!visit.dates || !visit.dates.includes(addOnItem.date)) return false

	if (visit?.type === ADDON_ONLY) {
		return true
	}

	if (visit?.type === GROOMING) return true

	if (visit.type === DAYCARE) {
		if (visit.early_dropoff) return true

		let addonsOnSameDayAsCheckIn = false

		if (visit?.checkin_time) {
			const checkin_date = moment(visit.checkin_time).hour(12).format('YYYY-MM-DD')
			const diff = moment(checkin_date, 'YYYY-MM-DD').diff(moment(addOnItem.date, 'YYYY-MM-DD'), 'days')
			if (diff === 0) {
				addonsOnSameDayAsCheckIn = true
			}
		}

		// if different day we don't care about time block
		if (!addonsOnSameDayAsCheckIn) return true

		// if on the same day as checkin, check if time block is allowed
		return allowedTimeBlocks.includes(addOnItem.time)
	}

	if (visit.type === BOARDING) {
		const first_day = visit.dates[0]
		const last_day = visit.dates[visit.dates.length - 1]

		const isOnFirstDay = addOnItem.date === first_day
		const isOnLastDay = addOnItem.date === last_day

		if (!isOnFirstDay && !isOnLastDay) return true

		if (isOnFirstDay) {
			if (visit.early_dropoff) return true
			if (visit.breakfast_available && addOnItem.time === 'morning') return true
			if (visit.first_day_daycare || (visit.visitTypeHasChanged && visit.newVisitType === DAYCARE))
				return ['midday', 'evening'].includes(addOnItem.time)
			return addOnItem.time === 'evening'
		}

		if (isOnLastDay) {
			if (visit.extended_pickup) return true
			if (visit.last_day_daycare) return ['midday', 'morning'].includes(addOnItem.time)
			return addOnItem.time === 'morning'
		}
	}

	if (visit?.type === 'compatibility_session') return true
}

// Got this from ChatGPT.
/**
 * Turns a number into a character based on 0 = A, 1 = B
 * @param {*} num Ideally an array cursor, so 0 -> A
 * @returns String
 */
export const convertNumberToCapitalLetter = (num) => {
	const charCode = num + 65 // Add 65 to convert 0 to 'A' and 1 to 'B'
	const capitalLetter = String.fromCharCode(charCode)
	return capitalLetter
}

export const formatBytes = (bytes) => {
	if (bytes < 1000000) {
		return (bytes / 1024).toFixed(2) + ' KB'
	} else {
		return (bytes / 1024 / 1024).toFixed(2) + ' MB'
	}
}

/**
 * Takes in an array of pet names and returns a string with an ampersand between the last two names
 * @param {string[]} pets
 * @returns string
 */
export const formatArrayOfPetNames = (pets = []) => {
	let formatted = ''

	if (pets.length === 0) {
		return formatted
	}
	if (pets.length === 1) {
		formatted = pets[0]
	}

	if (pets.length === 2) {
		formatted = pets.join(' & ')
	}

	if (pets.length > 2) {
		formatted = pets.join(', ')
		const lastComma = formatted.lastIndexOf(',')
		formatted = formatted.substring(0, lastComma) + ' &' + formatted.substring(lastComma + 1)
	}

	return formatted
}

/**
 * Given an object and a value, it will return the key that has that value.
 * Used when objects have more user friendly values for keys, but the key is needed for
 * api or other backend related things
 * @param {*} obj
 * @param {*} value
 * @returns
 */
export const getKeyForValueInObject = (obj, value) => {
	for (const key in obj) {
		if (obj[key] === value) {
			return key
		}
	}
	return ''
}

/**
 * Takes in a string, tests that it is only numbers, returns a value no greater than 100, or 0 if not a number
 * @param {string} value
 * @returns
 */
export const managePercentEntry = (value) => {
	const regex = /^\d+$/
	if (regex.test(value) === false) {
		return 0
	}

	const valueAsInt = parseInt(value, 10)
	if (valueAsInt > 100) {
		return 100
	}
	return valueAsInt
}

export const formatHoursOrDaysBeforeText = ({ hours = 0, displayAsDaysThreshold = 48 }) => {
	let text = ''
	if (hours > displayAsDaysThreshold && hours % 24 === 0) {
		text = `${hours / 24} days`
	} else {
		text = `${hours} hours`
	}
	return text
}
